import { Flex } from '@chakra-ui/react';
import { Avatar, AvatarFallback, AvatarImage, Card, CardContent, CardHeader, CardTitle } from '@mesh.ai/rabbit';
import { ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';

import { Divider } from 'uiToolkitV1/Divider';
import { TextBodyMedium, TextLabelSmall } from 'uiToolkitV1/Typography';

import { getRelevancyScoreColorLifecycle } from '../CandidateProgressTable';
import { LIFECYCLE_INSIGHTS_PROGRESS_TABLE_ROWS } from '../constants';

const data = LIFECYCLE_INSIGHTS_PROGRESS_TABLE_ROWS.map((row) => {
  const goalScore = row[2].value;
  const goalScoreFloat = parseFloat(goalScore.split('(')[1]);
  const competencyScore = row[3].value;
  const competencyScoreFloat = parseFloat(competencyScore.split('(')[1]);
  const confidenceScore = row[4].value;
  const profile = row[1].value;

  return {
    x: competencyScoreFloat,
    y: goalScoreFloat,
    profile,
    profileAvatarBorderColor: getRelevancyScoreColorLifecycle(confidenceScore),
    goalScore,
    goalScoreColor: getRelevancyScoreColorLifecycle(goalScore),
    competencyScore,
    competencyScoreColor: getRelevancyScoreColorLifecycle(competencyScore),
    confidenceScore,
    confidenceScoreColor: getRelevancyScoreColorLifecycle(confidenceScore),
  };
});

const RenderAvatar = ({ cx, cy, payload }) => {
  const tx = cx - 20;
  const ty = cy - 20;
  const {
    profile: { profilePhoto },
    profileAvatarBorderColor,
  } = payload || {};

  return (
    <g>
      {/* Border */}
      <circle cx={cx} cy={cy} r="20px" fill="none" stroke={profileAvatarBorderColor} strokeWidth="3px" />
      <image
        x={tx}
        y={ty}
        width={40}
        height={40}
        xlinkHref={profilePhoto}
        clipPath="inset(0% round 50%)"
        preserveAspectRatio="xMidYMid slice"
      />
    </g>
  );
};

const RenderTooltip = (tooltipData) => {
  const { payload } = tooltipData;
  const name = payload[0]?.payload?.profile.name;
  const profilePhoto = payload[0]?.payload?.profile.profilePhoto;
  const { goalScore, goalScoreColor, competencyScore, competencyScoreColor, confidenceScore, confidenceScoreColor } =
    payload[0]?.payload || {};
  const avatarFallback = name?.substring(0, 2);

  return (
    <Card style={{ minWidth: '200px', padding: '8px' }}>
      <CardContent>
        <Flex gap="8px" flexDir="column">
          <TextLabelSmall color="#333" fontWeight="600" padding="4px 12px">
            Average Progress
          </TextLabelSmall>
          <Flex
            padding="8px"
            flexDirection="column"
            justifyContent="center"
            alignItems="flex-start"
            gap="8px"
            alignSelf="stretch"
          >
            <Flex padding="2px 4px" flexDirection="column" alignItems="flex-start" gap="4px" alignSelf="stretch">
              <TextLabelSmall color="#666">Promotion confidence score</TextLabelSmall>
              <TextBodyMedium color={confidenceScoreColor} fontWeight="600">
                {confidenceScore}
              </TextBodyMedium>
            </Flex>
            <Flex>
              <Flex padding="2px 4px" flexDirection="column" alignItems="flex-start" gap="4px" alignSelf="stretch">
                <TextLabelSmall color="#666">Predicted goal score</TextLabelSmall>
                <TextBodyMedium color={goalScoreColor} fontWeight="600">
                  {goalScore}
                </TextBodyMedium>
              </Flex>
              <Flex padding="2px 4px" flexDirection="column" alignItems="flex-start" gap="4px" alignSelf="stretch">
                <TextLabelSmall color="#666">Predicted competency Score</TextLabelSmall>
                <TextBodyMedium color={competencyScoreColor} fontWeight="600">
                  {competencyScore}
                </TextBodyMedium>
              </Flex>
            </Flex>
          </Flex>
          <Divider profileAvatarBorderColor="#EBEFF3" />

          <Flex alignItems="center" gap="12px">
            <Avatar>
              <AvatarImage src={profilePhoto} alt="Avatar" />
              <AvatarFallback>{avatarFallback}</AvatarFallback>
            </Avatar>
            <Flex direction="column">
              <TextBodyMedium bold>{name}</TextBodyMedium>
            </Flex>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
};

export const LifecycleProgressChart = ({ title }) => (
  <Card style={{ height: '100%', padding: '12px', boxShadow: 'none' }}>
    <CardHeader>
      <CardTitle style={{ color: '#666', fontSize: '12px', fontWeight: 'bold' }}>{title}</CardTitle>
    </CardHeader>
    <CardContent style={{ height: 'calc(100% - 32px)' }}>
      {/* @ts-expect-error - union too complex */}
      <Flex w="100%" h="100%" alignItems="center">
        <TextLabelSmall color="#666666" position="absolute" right="60px" top="calc(50% - 200px)">
          Ready for promotion{' '}
        </TextLabelSmall>
        <ResponsiveContainer width="100%" height={400}>
          <ScatterChart width={500} height={400} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            {/* X-Axis with label outside */}
            <XAxis
              type="number"
              dataKey="x"
              name="X-axis"
              tick={false}
              label={{
                value: 'Predicted Competency Score →',
                position: 'insideBottom',
                fill: '#666666',
                style: { fontWeight: 'bold', fontSize: '10px' },
              }}
              axisLine={false}
              domain={[0, 100]}
            />

            {/* Y-Axis with rotated label outside */}
            <YAxis
              type="number"
              dataKey="y"
              tick={false}
              label={{
                value: ' Predicted Goal Score →',
                angle: -90,
                position: 'insideLeft',
                fill: '#666666',
                style: { textAnchor: 'middle', fontWeight: 'bold', fontSize: '10px' },
              }}
              axisLine={false}
              domain={[0, 100]}
            />

            <Tooltip cursor={false} content={RenderTooltip} />

            {/* Reference Lines to split chart into quadrants */}
            <ReferenceLine x={50} stroke="#EBEFF3" />
            <ReferenceLine y={50} stroke="#EBEFF3" />

            {/* Scatter plot data */}
            <Scatter name="Data Points" data={data} fill="#8884d8" shape={RenderAvatar} />
          </ScatterChart>
        </ResponsiveContainer>
      </Flex>
    </CardContent>
  </Card>
);
